<template>
  <div class="column-edit">
    <div class="column-edit__column">
      <field
        :value="pColumn.type"
        :type="'str'"
        :label="'Type'"
        :error="errors && errors.type"
        data-test-id="modal-input-field-type"
        :disabled="disabled"
        @update:value="update('type', $event)"
      />
      <field
        :value="pColumn.serial"
        :type="'str'"
        :label="'Serial'"
        :error="errors && errors.serial"
        :disabled="disabled"
        :after-slot-visible="!disabled"
        data-test-id="modal-input-field-serial"
        @update:value="update('serial', $event)"
      >
        <template #after>
          <button class="model-field__button model-field__button--in-field" @click="showModalScan">
            <i class="material-icons material-icon--16">photo_camera</i>
          </button>
          <button
            v-if="pColumn.serial"
            class="model-field__button model-field__button--in-field"
            @click="requestColumnData"
          >
            <i class="material-icons material-icon--16">transit_enterexit</i>
          </button>
        </template>
      </field>
      <field
        :value="pColumn.lot"
        :type="'str'"
        :label="'Lot'"
        data-test-id="modal-input-field-lot"
        :error="errors && errors.lot"
        :disabled="disabled"
        @update:value="update('lot', $event)"
      />
      <field
        :value="pColumn.max_pressure"
        :type="'int'"
        :label="'Max pressure, psi'"
        :error="errors && errors.max_pressure"
        :disabled="disabled"
        :hints="[500, 1000, 2000]"
        data-test-id="modal-input-field-required"
        @update:value="update('max_pressure', $event)"
      />
    </div>
    <div class="column-edit__column">
      <field
        :value="pColumn.length"
        :type="'int'"
        :label="'Length, mm'"
        :error="errors && errors.length"
        :disabled="disabled"
        :hints="[0, 25, 50, 100, 150]"
        data-test-id="modal-input-field-required"
        @update:value="update('length', $event)"
      />
      <field
        :value="pColumn.internal_diameter"
        :type="'float'"
        :label="'Internal diameter, mm'"
        :error="errors && errors.internal_diameter"
        :disabled="disabled || isZeroLength"
        :hints="[1, 2.1, 3.2, 4.6]"
        data-test-id="modal-input-field-required"
        @update:value="update('internal_diameter', $event)"
      />
      <field
        :value="pColumn.particle_size"
        :type="'float'"
        :label="'Particle size, μm'"
        :error="errors && errors.particle_size"
        :disabled="disabled || isZeroLength"
        :hints="[2, 2.7, 3.0, 5, 10]"
        data-test-id="modal-input-field-required"
        @update:value="update('particle_size', $event)"
      />
      <field
        :value="pColumn.pore_size"
        :type="'int'"
        :label="'Pore size, Å'"
        :error="errors && errors.pore_size"
        :disabled="disabled || isZeroLength"
        :hints="[60, 90, 100, 200, 300, 800, 1000, 2000]"
        data-test-id="modal-input-field-required"
        @update:value="update('pore_size', $event)"
      />
    </div>
    <div v-if="isZeroLength" class="column-edit__warning">
      <i class="material-icons material-icon--16">warning</i>
      <span>
        Column length 0 is a special value that tells the device to operate without a column.
        <br />
        In this mode, the other fields are disabled and set to 0.
      </span>
    </div>
    <ScanQRModal @data="autofillForm" />
  </div>
</template>

<script>
  import StandardModelFieldComponent from 'components/element/StandardModelFieldComponent.vue';
  import ColumnAPI from 'api/column';
  import ScanQRModal from 'components/block/modal/ScanQRModal';

  const UpdateColumnEvent = 'update:column';
  const EVENT_UPDATE_FIELD = 'updateField';
  const ZERO_LENGTH_FIELDS = ['internal_diameter', 'particle_size', 'pore_size'];

  export default {
    name: 'ColumnEditComponent',
    components: {
      ScanQRModal,
      field: StandardModelFieldComponent,
    },
    model: {
      prop: 'column',
      event: UpdateColumnEvent,
    },
    props: {
      column: Object,
      errors: Object,
      disabled: {
        type: Boolean,
        default: false,
      },
    },
    computed: {
      pColumn() {
        return this.column ? this.column : {};
      },
      isZeroLength() {
        return this.pColumn.length === 0;
      },
    },
    methods: {
      normalizeZeroLengthColumn(column) {
        if (column.length === 0) {
          ZERO_LENGTH_FIELDS.forEach((field) => {
            column[field] = 0;
          });
        }
        return column;
      },
      update(key, value) {
        const column = this.normalizeZeroLengthColumn({
          ...this.pColumn,
          [key]: value,
        });

        this.$emit(UpdateColumnEvent, column);
        this.$emit(EVENT_UPDATE_FIELD, key);

        if (key === 'length' && value === 0) {
          ZERO_LENGTH_FIELDS.forEach((field) => this.$emit(EVENT_UPDATE_FIELD, field));
        }
      },
      requestColumnData() {
        const onSuccess = (data) => {
          const column = Object.keys(this.pColumn)
            .filter((key) => key in data)
            .reduce(
              (updatedColumn, key) => ({
                ...updatedColumn,
                [key]: data[key],
              }),
              { ...this.pColumn },
            );

          this.$emit(UpdateColumnEvent, this.normalizeZeroLengthColumn(column));
        };

        ColumnAPI.getHplcproductionData(this.column.serial, onSuccess, this.notifyResponseError);
      },

      showModalScan() {
        this.$modal.show('scan-qr-modal');
      },

      parseColumnLink(link) {
        const result = link.match(/https?:\/\/.+?\/(?<serial>\w+)\/?/);
        if (result?.groups) {
          return result.groups;
        }
        this.notifyError('Link is illegal');
        throw new Error('Link is illegal');
      },

      async autofillForm(link) {
        const { serial } = this.parseColumnLink(link);

        this.update('serial', serial);
        await this.$nextTick();
        this.requestColumnData();
      },
    },
  };
</script>

<style scoped lang="scss">
  .column-edit {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: space-between;

    @media (max-width: $screen-xs-max) {
      flex-direction: column;
    }

    &__column {
      flex: 1 0;
    }

    &__column:not(:first-child) {
      margin-left: 16px;

      @media (max-width: $screen-xs-max) {
        margin-left: 0;
      }
    }

    .standard-model-field {
      margin-top: 8px;
      display: block;
    }

    &__warning {
      flex: 0 0 100%;
      display: flex;
      align-items: flex-start;
      gap: 8px;
      margin-top: 16px;
      padding: 10px 12px;
      background-color: $color-bg-warning;
      color: $color-text-default;
      border-radius: $border-radius__md;
    }
  }
</style>
